import { useCallback, useEffect, useRef, useState } from 'react';

import { messageService } from '@classes/messageService';

import { useStopwatch } from './useStopwatch';

/**
 * Контейнеры в порядке предпочтения. `MediaRecorder` без явного mimeType пишет
 * тем, что выберет браузер (Chrome — webm/opus, Safari — mp4), поэтому тип
 * фиксируем сами и потом честно проставляем его блобу.
 */
const AUDIO_MIME_CANDIDATES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
];

const VIBRATION_MS = 100;
const START_CUE_FREQUENCY = 880;
const START_CUE_DURATION_S = 0.12;
const START_CUE_VOLUME = 0.05;

const pickAudioMimeType = (): string =>
    AUDIO_MIME_CANDIDATES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? '';

const vibrateDevice = () => {
    if (!navigator.vibrate) return;
    navigator.vibrate([VIBRATION_MS]);
};

/** Короткий сигнал старта — локально, без запроса на сторонний хост. */
const playStartCue = () => {
    if (!window.AudioContext) return;

    try {
        const audioContext = new AudioContext();
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();

        oscillator.frequency.value = START_CUE_FREQUENCY;
        gain.gain.value = START_CUE_VOLUME;
        oscillator.connect(gain).connect(audioContext.destination);
        oscillator.onended = () => audioContext.close();
        oscillator.start();
        oscillator.stop(audioContext.currentTime + START_CUE_DURATION_S);
    } catch {
        // звук — необязательная часть сценария, запись важнее
    }
};

export interface RecordedAudio {
    blob: Blob;
    /** Фактический MIME контейнера, выбранного браузером. */
    mimeType: string;
    /** Длительность в секундах: у webm её нет в заголовке, считаем секундомером. */
    duration: number;
}

interface Props {
    /** Вызывается один раз после остановки, когда данные готовы. */
    onRecorded: (audio: RecordedAudio) => void;
}

/**
 * Запись голосового сообщения: доступ к микрофону, контейнер, пауза,
 * секундомер и гарантированное освобождение устройства.
 *
 * Хук общий для чата заказа и карточки задачи — раньше в обоих местах
 * лежала своя копия этой логики.
 */
export const useAudioRecorder = ({ onRecorded }: Props) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    const { time, elapsedSeconds } = useStopwatch({ isRunning: isRecording, isPaused });

    const recorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const elapsedRef = useRef(0);
    const recordedDurationRef = useRef(0);
    const onRecordedRef = useRef(onRecorded);

    elapsedRef.current = elapsedSeconds;
    onRecordedRef.current = onRecorded;

    /** Без этого индикатор записи в браузере продолжает гореть. */
    const releaseStream = useCallback(() => {
        if (!streamRef.current) return;
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    }, []);

    const start = useCallback(async () => {
        if (recorderRef.current) return;

        vibrateDevice();

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            playStartCue();
            streamRef.current = stream;

            const mimeType = pickAudioMimeType();
            const recorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);

            recorder.ondataavailable = (event) => {
                if (!event.data.size) return;

                const type = recorder.mimeType || event.data.type;

                onRecordedRef.current({
                    blob: new Blob([event.data], { type }),
                    mimeType: type,
                    duration: recordedDurationRef.current,
                });
            };

            recorder.start();
            recorderRef.current = recorder;
            setIsRecording(true);
            setIsPaused(false);
        } catch (error) {
            releaseStream();
            console.error('Ошибка доступа к микрофону:', error);
            messageService.sendError('Нет доступа к микрофону');
        }
    }, [releaseStream]);

    const pause = useCallback(() => {
        if (!recorderRef.current || recorderRef.current.state !== 'recording') return;
        recorderRef.current.pause();
        setIsPaused(true);
    }, []);

    const resume = useCallback(() => {
        if (!recorderRef.current || recorderRef.current.state !== 'paused') return;
        recorderRef.current.resume();
        setIsPaused(false);
    }, []);

    const stop = useCallback(() => {
        const recorder = recorderRef.current;
        if (!recorder) return;

        // длительность фиксируем до сброса секундомера: `ondataavailable`
        // сработает асинхронно, когда счётчик уже обнулится
        recordedDurationRef.current = elapsedRef.current;

        recorder.stop();
        recorderRef.current = null;

        setIsRecording(false);
        setIsPaused(false);
        releaseStream();
        vibrateDevice();
    }, [releaseStream]);

    // размонтирование посреди записи не должно оставлять микрофон включённым
    useEffect(
        () => () => {
            if (recorderRef.current && recorderRef.current.state !== 'inactive') {
                recorderRef.current.ondataavailable = null;
                recorderRef.current.stop();
            }
            recorderRef.current = null;
            releaseStream();
        },
        [releaseStream]
    );

    return {
        isRecording,
        isPaused,
        /** Готовая строка секундомера: `mm:ss`, а при необходимости `h:mm:ss`. */
        time,
        elapsedSeconds,
        start,
        pause,
        resume,
        stop,
    };
};
